import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import {
  GUIDE_FILE_ACCEPT,
  readGuideFile,
  type GuideFileReadResult,
} from "../../utils/guideFileReader";

interface IntakeAssessmentFormProps {
  name: string;
  actors: string;
  requirement: string;
  submitting: boolean;
  sessionReady: boolean;
  error: string | null;
  onNameChange: (value: string) => void;
  onActorsChange: (value: string) => void;
  onRequirementChange: (value: string) => void;
  onCancel: () => void;
  onSubmit: (relatedDocsMarkdown?: string) => void;
}

const IntakeAssessmentForm: React.FC<IntakeAssessmentFormProps> = ({
  name,
  actors,
  requirement,
  submitting,
  sessionReady,
  error,
  onNameChange,
  onActorsChange,
  onRequirementChange,
  onCancel,
  onSubmit,
}) => {
  const [files, setFiles] = useState<GuideFileReadResult[]>([]);
  const [converting, setConverting] = useState(false);
  const [convertError, setConvertError] = useState<string | null>(null);

  const handleAttach = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files ?? []);
    e.target.value = "";
    if (picked.length === 0) return;
    setConverting(true);
    setConvertError(null);
    try {
      const converted: GuideFileReadResult[] = [];
      for (const file of picked) {
        converted.push(await readGuideFile(file));
      }
      setFiles((prev) => [...prev, ...converted]);
    } catch (err: unknown) {
      setConvertError(
        err instanceof Error ? err.message : "Could not convert attachment to Markdown",
      );
    } finally {
      setConverting(false);
    }
  };

  const relatedDocs =
    files.length === 0
      ? undefined
      : files.map((f) => `### ${f.fileName}\n\n${f.text}`).join("\n\n");

  const busy = submitting || converting;
  const canSubmit = requirement.trim().length > 0 && sessionReady && !busy;

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Typography variant="h6" sx={{ mb: 0.5 }}>
        Describe the intake
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Fill in the need in plain language. You do not have to name existing APIs — the
        assessment searches the catalog for you. Chat comes after you see the score.
      </Typography>
      <Stack spacing={2.5}>
        <TextField
          label="Intake name"
          placeholder="e.g. Employee profile directory"
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
          helperText="Shown in the past-assessments list. You can leave this blank for now."
          fullWidth
        />
        <TextField
          label="Who needs this?"
          placeholder="e.g. HR managers, employees, partner apps"
          value={actors}
          onChange={(e) => onActorsChange(e.target.value)}
          helperText="Actors or systems that will call the API."
          fullWidth
        />
        <TextField
          label="What should they be able to do?"
          placeholder="e.g. Create an employee profile, list profiles, fetch one by id, and deactivate when someone leaves."
          value={requirement}
          onChange={(e) => onRequirementChange(e.target.value)}
          helperText="List the operations in plain language. Resource + verb works well (create profile, list profiles)."
          required
          fullWidth
          multiline
          minRows={5}
        />
        <Box>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Related documents (optional)
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Tooltip title="Attach a brief, ticket, or spec (text, Markdown, JSON, YAML, or convert via CMS)">
              <IconButton
                component="label"
                disabled={busy}
                aria-label="Attach related documents"
              >
                <AttachFileIcon />
                <input
                  type="file"
                  hidden
                  multiple
                  accept={GUIDE_FILE_ACCEPT}
                  onChange={handleAttach}
                />
              </IconButton>
            </Tooltip>
            <Typography variant="caption" color="text.secondary">
              Optional. Attach a brief or notes if you have them.
            </Typography>
          </Stack>
          {files.length > 0 && (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 1 }}>
              {files.map((f, i) => (
                <Chip
                  key={`${f.fileName}-${i}`}
                  size="small"
                  label={f.fileName}
                  onDelete={() => setFiles((prev) => prev.filter((_, idx) => idx !== i))}
                />
              ))}
            </Stack>
          )}
        </Box>
        {(error || convertError) && (
          <Alert severity="error">{convertError || error}</Alert>
        )}
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button onClick={onCancel} disabled={busy} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => onSubmit(relatedDocs)}
            disabled={!canSubmit}
            sx={{ textTransform: "none" }}
          >
            {busy ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={16} color="inherit" />
                <span>{converting ? "Reading files…" : "Starting assessment…"}</span>
              </Stack>
            ) : (
              "Run assessment"
            )}
          </Button>
        </Stack>
        {sessionReady ? (
          <Typography variant="caption" color="text.secondary">
            This sends one request to the assistant. After the score appears you can open a
            follow-up chat.
          </Typography>
        ) : (
          <Typography variant="caption" color="text.secondary">
            Connecting to the intake assistant…
          </Typography>
        )}
      </Stack>
    </Paper>
  );
};

export default IntakeAssessmentForm;
